import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Sparkles, TrendingUp, Users, Snowflake } from "lucide-react";
import { toast } from "sonner";

/**
 * RecapStreakBadge — displays the two-way accountability streak with an
 * inline "Freeze this week" button (max 2/quarter).
 */
export default function RecapStreakBadge() {
  const [data, setData] = useState(null);
  const [freezes, setFreezes] = useState(null);
  const [freezing, setFreezing] = useState(false);

  const load = () => {
    api.get("/community/recap-streak")
      .then(({ data }) => setData(data))
      .catch(() => {});
    api.get("/community/streak-freezes")
      .then(({ data }) => setFreezes(data))
      .catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!data) return null;
  const s = data.streak || 0;
  const active = s > 0;
  const quarterUsed = freezes?.quarter_used ?? 0;
  const quarterCap = freezes?.quarter_cap ?? 2;
  const canFreeze = quarterUsed < quarterCap;

  const freezeThisWeek = async () => {
    setFreezing(true);
    try {
      const { data } = await api.post("/community/streak-freeze", {});
      toast.success(
        data?.already
          ? "Already frozen this week"
          : `Week frozen · ${data.quarter_used}/${data.quarter_cap} used this quarter`
      );
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setFreezing(false);
    }
  };

  return (
    <div
      className={[
        "border p-4 flex flex-col sm:flex-row items-stretch sm:items-start gap-3 sm:gap-4",
        active
          ? "border-teal-800 bg-teal-950/30"
          : "border-slate-200 bg-slate-50/40",
      ].join(" ")}
      data-testid="recap-streak-badge"
    >
      {/* Row 1 on mobile: icon + eyebrow inline; on desktop icon stays in its own column */}
      <div className="flex items-center gap-3 sm:block sm:shrink-0">
        <div className={`w-12 h-12 flex items-center justify-center border shrink-0 ${active ? "border-teal-700 bg-teal-950" : "border-slate-200 bg-slate-50"}`}>
          <Sparkles className={`w-6 h-6 ${active ? "text-teal-400" : "text-slate-400"}`} />
        </div>
        {/* Mobile-only inline eyebrow so it reads next to the icon rather than being crammed sideways */}
        <div className="sm:hidden font-mono-num text-[10px] uppercase tracking-[0.2em] text-teal-400 flex items-center gap-1.5">
          <Users className="w-3 h-3 shrink-0" /> Two-way accountability
        </div>
      </div>

      <div className="min-w-0 flex-1">
        {/* Desktop eyebrow (hidden on mobile — shown next to the icon above) */}
        <div className="hidden sm:flex font-mono-num text-[10px] uppercase tracking-[0.3em] text-teal-400 items-center gap-1.5">
          <Users className="w-3 h-3" /> // Two-way accountability
        </div>
        <div className="flex items-baseline gap-2 mt-1 flex-wrap">
          <span className="font-display font-black text-3xl leading-none text-slate-900 dark:text-white tabular-nums">
            {s}
          </span>
          <span className="font-mono-num text-[11px] uppercase tracking-widest text-slate-600">
            week{s === 1 ? "" : "s"} · recap streak
          </span>
          {data.frozen_weeks > 0 && (
            <span className="inline-flex items-center gap-0.5 font-mono-num text-[10px] uppercase tracking-widest text-cyan-400 ml-1" data-testid="recap-frozen-count">
              <Snowflake className="w-3 h-3" /> {data.frozen_weeks} frozen
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 mt-1.5 leading-relaxed">
          {active
            ? "You've shared a weekly recap AND cheered someone else — every week — for the past "
              + s + " week" + (s === 1 ? "" : "s") + ". Keep the loop alive."
            : (
                <>
                  Share your weekly recap <em>and</em> react on someone else's to start a streak.
                  <span className="block text-slate-400 mt-1 font-mono-num text-[10px] uppercase tracking-widest">
                    tip · Head to /history and tap "Share my week"
                  </span>
                </>
              )}
        </div>
      </div>

      {/* Right column on desktop, full-width bottom row on mobile */}
      <div className="flex items-center justify-between sm:flex-col sm:items-end sm:justify-start gap-2 sm:shrink-0">
        {active && <TrendingUp className="w-6 h-6 text-teal-500 hidden sm:block" />}
        <button
          type="button"
          onClick={freezeThisWeek}
          disabled={!canFreeze || freezing}
          className={[
            "inline-flex items-center gap-1.5 border px-3 py-1.5 font-mono-num text-[10px] uppercase tracking-widest transition-colors w-full sm:w-auto justify-center",
            canFreeze
              ? "border-cyan-800 bg-cyan-950/30 text-cyan-300 hover:bg-cyan-900/40"
              : "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed",
          ].join(" ")}
          data-testid="streak-freeze-btn"
          title={canFreeze ? "Freeze this week (travel/sickness) — max 2 per quarter" : "You've used both freezes this quarter"}
        >
          <Snowflake className="w-3 h-3" /> {freezing ? "Freezing…" : `Freeze week (${quarterUsed}/${quarterCap})`}
        </button>
      </div>
    </div>
  );
}
